import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { permissionsAPI, usersAPI } from '../../services/api';

export default function StaffPermissions() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const feedback = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true); setUser(null); setError(''); setSuccess('');
    Promise.all([usersAPI.list(), permissionsAPI.catalog(), permissionsAPI.getForUser(userId)])
      .then(([staff, definitions, grants]) => {
        if (!active) return;
        const target = (staff.users || []).find((entry) => String(entry.id) === userId);
        if (!target) throw new Error('Staff account not found. Return to Staff & Approvers and select an account.');
        setUser(target);
        setCatalog(definitions.catalog || []);
        setSelected(grants.permissions || []);
        setSaved(grants.permissions || []);
      })
      .catch((err) => { if (active) setError(err.response?.data?.error || err.message || 'Failed to load permissions.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);

  useEffect(() => {
    if (error || success) {
      feedback.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      feedback.current?.focus({ preventScroll: true });
    }
  }, [error, success]);

  const isAdmin = user?.role === 'admin';
  const dirty = selected.length !== saved.length || selected.some((key) => !saved.includes(key));
  const disabled = loading || saving || !user || isAdmin || !dirty;

  function toggle(key) {
    setSuccess(''); setError('');
    setSelected((previous) => previous.includes(key) ? previous.filter((item) => item !== key) : [...previous, key]);
  }

  async function handleSave() {
    if (disabled) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      const result = await permissionsAPI.setForUser(userId, selected);
      const confirmed = result.permissions || selected;
      setSelected(confirmed); setSaved(confirmed);
      setSuccess(`Permissions saved successfully for ${user.full_name}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save permissions. Your changes are still here; please try again.');
    } finally {
      setSaving(false);
    }
  }

  const saveButton = (
    <button type="button" onClick={handleSave} disabled={disabled}
      className="rounded-xl bg-orange-600 px-6 py-3 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-50">
      {saving ? 'Saving…' : 'Save Permissions'}
    </button>
  );

  return (
    <section className="space-y-6" aria-busy={loading || saving}>
      <Link to="/staff" className="inline-block text-sm font-semibold text-orange-700 hover:underline">← Back to Staff & Approvers</Link>
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Staff Permissions</h1>
          {user && <p className="mt-2 text-lg font-semibold text-slate-800">{user.full_name}</p>}
          {user && <p className="mt-1 break-all text-sm text-slate-500">{user.email} · {user.role}</p>}
        </div>
        {saveButton}
      </header>

      <div ref={feedback} tabIndex={-1} className="outline-none">
        {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 font-medium text-red-800">{error}</p>}
        {success && <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 font-semibold text-emerald-800">✓ {success}</p>}
      </div>

      {loading ? <p className="py-10 text-center text-slate-500">Loading permissions…</p> : user && <>
        <p className="text-sm text-slate-600">Select additional access for this staff member. Their existing role access still applies.</p>
        {isAdmin && <p className="rounded-xl border border-purple-200 bg-purple-50 p-4 text-purple-800">This administrator already has access to every capability.</p>}
        <fieldset disabled={saving || isAdmin} className="grid gap-3 md:grid-cols-2">
          <legend className="mb-3 text-sm font-semibold text-slate-700">Available permissions · {isAdmin ? catalog.length : selected.length} selected</legend>
          {catalog.map((item) => (
            <label key={item.key} className={`flex items-start gap-3 rounded-xl border p-4 ${selected.includes(item.key) || isAdmin ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white'} ${saving || isAdmin ? 'opacity-60' : 'cursor-pointer hover:border-orange-300'}`}>
              <input type="checkbox" checked={isAdmin || selected.includes(item.key)} onChange={() => toggle(item.key)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-orange-600 focus:ring-orange-500" />
              <span className="text-sm leading-6 text-slate-800">{item.label}</span>
            </label>
          ))}
        </fieldset>
        <footer className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-600">{saving ? 'Saving permissions…' : dirty ? 'You have unsaved changes.' : success ? 'All changes saved.' : 'No unsaved changes.'}</p>
          {saveButton}
        </footer>
      </>}
    </section>
  );
}
